import React, { useState, useEffect } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { MyVacationsCard } from '../components/vacations/MyVacationsCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { vacationsAPI } from '../lib/api';
import { ApprovalChainView } from '../components/ApprovalChainView';
import { RemoveDaysDialog } from '../components/vacations/RemoveDaysDialog';
import {
  Check,
  X,
  Clock,
  Calendar,
  User,
  Loader2,
  CalendarHeart,
  AlertTriangle,
  MapPin,
  Users,
  Palmtree,
  UserX,
  History,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

export default function VacationRequests({ embedded = false }) {
  const { hasPermission } = useAuth();
  const canApprove = hasPermission('vacations', 'execute');
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedVacation, setSelectedVacation] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [conflicts, setConflicts] = useState({});
  const [editDaysVacation, setEditDaysVacation] = useState(null);

  // Deep-Link Handling: ?antrag=<vacation_id> öffnet die Karte direkt
  const [searchParams] = useSearchParams();
  const focusVacationId = searchParams.get('antrag');
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    if (canApprove) {
      loadVacations();
    } else {
      setLoading(false);
    }
  }, [canApprove]);

  // Scroll zur Karte sobald sie gerendert ist und blende sie kurz hervor
  useEffect(() => {
    if (!focusVacationId || loading) return;
    const target = document.querySelector(`[data-vacation-card="${focusVacationId}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(focusVacationId);
    const timer = setTimeout(() => setHighlightId(null), 2200);
    return () => clearTimeout(timer);
  }, [focusVacationId, loading, vacations]);

  const loadVacations = async () => {
    try {
      const response = await vacationsAPI.getPending();
      setVacations(response.data);
      // Load conflicts for all pending vacations
      const conflictResults = {};
      await Promise.all(
        response.data.map(async (v) => {
          try {
            const res = await vacationsAPI.getConflicts(v.id);
            conflictResults[v.id] = res.data;
          } catch {
            conflictResults[v.id] = { conflicts: [], location_name: null };
          }
        })
      );
      setConflicts(conflictResults);
    } catch (error) {
      console.error('Failed to load vacations:', error);
      toast.error('Fehler beim Laden der Anträge');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessingId(id);
    try {
      await vacationsAPI.approve(id, { approved: true });
      toast.success('Urlaubsantrag genehmigt');
      setVacations(vacations.filter((v) => v.id !== id));
      const newConflicts = { ...conflicts };
      delete newConflicts[id];
      setConflicts(newConflicts);
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (vacation) => {
    setSelectedVacation(vacation);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedVacation) return;
    
    setProcessingId(selectedVacation.id);
    try {
      await vacationsAPI.approve(selectedVacation.id, {
        approved: false,
        rejection_reason: rejectionReason || undefined,
      });
      toast.success('Urlaubsantrag abgelehnt');
      setVacations(vacations.filter((v) => v.id !== selectedVacation.id));
      const newConflicts = { ...conflicts };
      delete newConflicts[selectedVacation.id];
      setConflicts(newConflicts);
      setRejectDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6 animate-fade-in" data-testid="vacation-requests-page">
        {/* Eigene Antraege — sichtbar fuer alle */}
        <MyVacationsCard />

        {canApprove && (
          <>
            {/* Header — nur fuer Manager mit Genehmigungs-Recht */}
            <div>
              <h1 className="font-heading font-bold text-lg sm:text-xl text-foreground" data-testid="approval-queue-heading">
                Urlaubsanträge
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {vacations.length} offene Anträge zur Bearbeitung
              </p>
            </div>

            {/* Requests List */}
            {vacations.length > 0 ? (
          <div className="space-y-4">
            {vacations.map((vacation, index) => {
              const conflictData = conflicts[vacation.id];
              const conflictList = conflictData?.conflicts || [];
              const locationName = conflictData?.location_name;
              const hasConflicts = conflictList.length > 0;

              return (
                <Card
                  key={vacation.id}
                  className={`border-border/60 animate-slide-up transition-all duration-500 ${
                    highlightId === vacation.id
                      ? 'ring-2 ring-amber-500 ring-offset-2 shadow-lg shadow-amber-500/20 scale-[1.01]'
                      : ''
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`vacation-request-${vacation.id}`}
                  data-vacation-card={vacation.id}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="space-y-3 flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-lg">
                              {vacation.employee_name}
                            </h3>
                            <Badge className="status-pending">
                              <Clock className="mr-1 h-3 w-3" />
                              Ausstehend
                            </Badge>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>
                              {formatDate(vacation.start_date)} - {formatDate(vacation.end_date)}
                            </span>
                          </div>
                          <div>
                            <Badge variant="secondary">
                              {vacation.days_count} Tag{vacation.days_count !== 1 ? 'e' : ''}
                            </Badge>
                          </div>
                          <div>
                            <Badge variant="outline">{vacation.vacation_type}</Badge>
                          </div>
                          {vacation.is_retroactive && (
                            <div>
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" title={vacation.retroactive_by ? `Erfasst von ${vacation.retroactive_by}` : undefined} data-testid="retroactive-badge">
                                <History className="h-3 w-3 mr-1" /> rückwirkend erfasst
                              </Badge>
                            </div>
                          )}
                        </div>

                        {vacation.notes && (
                          <p className="text-sm text-muted-foreground bg-secondary/30 p-3 rounded-lg">
                            {vacation.notes}
                          </p>
                        )}

                        {vacation.holidays_saved > 0 && (
                          <div className="flex items-center gap-2 text-green-600 text-sm">
                            <CalendarHeart className="h-4 w-4" />
                            <span>{vacation.holidays_saved} Feiertag{vacation.holidays_saved > 1 ? 'e' : ''} im Zeitraum (nicht berechnet)</span>
                          </div>
                        )}

                        {/* Conflict Warning */}
                        {hasConflicts && (
                          <div
                            className={`p-3 rounded-lg border ${
                              conflictList.length >= 3
                                ? 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700'
                                : 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 dark:border-amber-700'
                            }`}
                            data-testid={`conflict-warning-${vacation.id}`}
                          >
                            <div className="flex items-start gap-2">
                              <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                                conflictList.length >= 3 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold ${
                                  conflictList.length >= 3 ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'
                                }`}>
                                  {conflictList.length} Überschneidung{conflictList.length !== 1 ? 'en' : ''}
                                  {locationName && (
                                    <span className="font-normal"> am Standort {locationName}</span>
                                  )}
                                </p>
                                <div className="mt-1.5 space-y-1">
                                  {conflictList.map((c) => (
                                    <div key={`${c.employee_name}-${c.start_date}-${c.type}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                                      {c.type === 'vacation' ? (
                                        <Palmtree className="h-3 w-3 flex-shrink-0 text-emerald-600" />
                                      ) : (
                                        <UserX className="h-3 w-3 flex-shrink-0 text-red-500" />
                                      )}
                                      <span className="font-medium">{c.employee_name}</span>
                                      <span>—</span>
                                      <span>{c.type_label}</span>
                                      <span className="text-muted-foreground/70">
                                        ({formatDate(c.start_date)} - {formatDate(c.end_date)})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground">
                          Eingereicht am {formatDate(vacation.created_at)}
                        </p>

                        <div className="mt-3 pt-3 border-t border-border/60">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Freigabe-Kette</p>
                          <ApprovalChainView vacationId={vacation.id} compact />
                        </div>
                      </div>

                      <div className="flex gap-2 md:flex-col flex-shrink-0">
                        <Button
                          onClick={() => handleApprove(vacation.id)}
                          disabled={processingId === vacation.id}
                          className="rounded-full flex-1 md:flex-none"
                          data-testid={`approve-vacation-${vacation.id}`}
                        >
                          {processingId === vacation.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Genehmigen
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => openRejectDialog(vacation)}
                          disabled={processingId === vacation.id}
                          className="rounded-full flex-1 md:flex-none text-destructive hover:text-destructive"
                          data-testid={`reject-vacation-${vacation.id}`}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Ablehnen
                        </Button>
                        {vacation.days_count > 1 && (
                          <Button
                            variant="ghost"
                            onClick={() => setEditDaysVacation(vacation)}
                            disabled={processingId === vacation.id}
                            className="rounded-full flex-1 md:flex-none"
                            data-testid={`edit-days-vacation-${vacation.id}`}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            Tage entfernen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-heading font-semibold text-lg">Alles erledigt!</h3>
                <p className="text-muted-foreground mt-1">
                  Es gibt keine offenen Urlaubsanträge zur Bearbeitung.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Urlaubsantrag ablehnen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Möchten Sie den Urlaubsantrag von <strong>{selectedVacation?.employee_name}</strong> wirklich ablehnen?
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Ablehnungsgrund (optional)</label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Grund für die Ablehnung..."
                  rows={3}
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setRejectDialogOpen(false)}
                  className="rounded-full"
                >
                  Abbrechen
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleReject}
                  disabled={processingId === selectedVacation?.id}
                  className="rounded-full"
                  data-testid="confirm-reject-btn"
                >
                  {processingId === selectedVacation?.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <X className="mr-2 h-4 w-4" />
                      Ablehnen
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <RemoveDaysDialog
          open={!!editDaysVacation}
          onOpenChange={(o) => { if (!o) setEditDaysVacation(null); }}
          vacation={editDaysVacation}
          onDone={loadVacations}
        />
      </div>
    </MaybeLayout>
  );
}
